import React, { useState, useMemo, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Plus, Save, X, Pencil, Trash2, UtensilsCrossed, Wine, Eye, EyeOff, GripVertical } from 'lucide-react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SectionToggle = ({ value, onChange, testIdPrefix }) => (
  <div className="flex rounded-xl overflow-hidden border border-[#E5DCD3]">
    <button type="button" onClick={() => onChange('cuisine')}
      className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${value === 'cuisine' ? 'bg-[#E16833] text-white' : 'bg-white text-[#6E5E52] hover:bg-[#F3EDE5]'}`}
      data-testid={`${testIdPrefix}-section-cuisine`}>
      <UtensilsCrossed className="w-3.5 h-3.5" /> Cuisine
    </button>
    <button type="button" onClick={() => onChange('bar')}
      className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${value === 'bar' ? 'bg-[#1B3A4B] text-white' : 'bg-white text-[#6E5E52] hover:bg-[#F3EDE5]'}`}
      data-testid={`${testIdPrefix}-section-bar`}>
      <Wine className="w-3.5 h-3.5" /> Bar
    </button>
  </div>
);

const SectionBadge = ({ section }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${section === 'bar' ? 'bg-[#1B3A4B]/10 text-[#1B3A4B]' : 'bg-[#E16833]/10 text-[#E16833]'}`}>
    {section === 'bar' ? <Wine className="w-3 h-3" /> : <UtensilsCrossed className="w-3 h-3" />}
    {section === 'bar' ? 'Bar' : 'Cuisine'}
  </span>
);

const SortableCategoryRow = ({ cat, editingCat, setEditingCat, updateCategory, deleteCategory, toggleCategoryActive, adminRole }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: cat.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 50 : 'auto' };
  const isInactive = cat.is_active === false;

  return (
    <div ref={setNodeRef} style={style}
      className={`bg-white rounded-2xl p-4 border flex items-center gap-3 transition-opacity ${isInactive ? 'border-red-200 opacity-60' : 'border-[#E5DCD3]'} ${isDragging ? 'shadow-xl ring-2 ring-[#D4A853]' : ''}`}
      data-testid={`category-row-${cat.id}`}>
      {/* Drag handle */}
      <div {...attributes} {...listeners}
        className="flex items-center justify-center cursor-grab active:cursor-grabbing touch-none px-0.5"
        data-testid={`cat-drag-handle-${cat.id}`}>
        <GripVertical className="w-5 h-5 text-[#D4A853]" />
      </div>
      <div className="w-8 h-8 bg-[#F3EDE5] rounded-lg flex items-center justify-center text-sm font-bold text-[#6E5E52]">
        {cat.display_order}
      </div>
      <div className="flex-1 min-w-0">
        {editingCat?.id === cat.id ? (
          <div className="space-y-2">
            <div className="flex flex-col sm:flex-row gap-2">
              <Input value={editingCat.name} onChange={(e) => setEditingCat({ ...editingCat, name: e.target.value })}
                className="rounded-lg border-[#E5DCD3] text-sm" placeholder="Name (EN)" data-testid={`edit-cat-name-${cat.id}`} />
              <Input value={editingCat.name_fr} onChange={(e) => setEditingCat({ ...editingCat, name_fr: e.target.value })}
                className="rounded-lg border-[#E5DCD3] text-sm" placeholder="Nom (FR)" />
            </div>
            <div className="flex items-center gap-2">
              <SectionToggle value={editingCat.section || 'cuisine'}
                onChange={(s) => setEditingCat({ ...editingCat, section: s })}
                testIdPrefix={`edit-cat-${cat.id}`} />
              <div className="flex gap-1 ml-auto">
                <Button size="sm" onClick={() => updateCategory(cat.id, { name: editingCat.name, name_fr: editingCat.name_fr, display_order: editingCat.display_order, section: editingCat.section })}
                  className="bg-[#0A422D] hover:bg-[#083323] text-white rounded-lg" data-testid={`save-cat-${cat.id}`}>
                  <Save className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingCat(null)} className="rounded-lg">
                  <X className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div>
              <p className={`font-semibold text-sm ${isInactive ? 'text-red-400 line-through' : 'text-[#2D241D]'}`}>
                {cat.name_fr || cat.name}
              </p>
              <p className="text-xs text-[#6E5E52]">{cat.name_fr !== cat.name ? cat.name : ''}{cat.name_fr !== cat.name ? ' - ' : ''}{cat.item_count || 0} articles</p>
            </div>
            <SectionBadge section={cat.section || 'cuisine'} />
            {isInactive && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-semibold">MASQUEE</span>
            )}
          </div>
        )}
      </div>
      {editingCat?.id !== cat.id && (
        <div className="flex gap-1">
          <Button size="sm" variant="ghost"
            onClick={() => toggleCategoryActive(cat.id, cat.is_active !== false)}
            className={isInactive ? 'text-[#2D6A4F] hover:text-[#2D6A4F]' : 'text-orange-500 hover:text-orange-600'}
            data-testid={`toggle-cat-${cat.id}`}>
            {isInactive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setEditingCat({ ...cat })}
            className="text-[#6E5E52] hover:text-[#0A422D]" data-testid={`edit-cat-btn-${cat.id}`}>
            <Pencil className="w-4 h-4" />
          </Button>
          {adminRole === 'admin' && (
            <Button size="sm" variant="ghost" onClick={() => deleteCategory(cat.id)}
              className="text-[#E63946] hover:text-[#E63946]" data-testid={`delete-cat-btn-${cat.id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export const CategoriesTab = ({
  categories, showNewCat, setShowNewCat, newCat, setNewCat,
  createCategory, editingCat, setEditingCat, updateCategory,
  deleteCategory, moveCategoryOrder, adminRole,
}) => {
  const [reorderSaving, setReorderSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const sortedCats = useMemo(() =>
    [...categories].sort((a, b) => (a.display_order || 999) - (b.display_order || 999)),
    [categories]
  );

  const catIds = useMemo(() => sortedCats.map(c => c.id), [sortedCats]);

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sortedCats.findIndex(c => c.id === active.id);
    const newIndex = sortedCats.findIndex(c => c.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(sortedCats, oldIndex, newIndex);
    const payload = reordered.map((cat, i) => ({ id: cat.id, display_order: i }));
    setReorderSaving(true);
    try {
      await axios.post(`${API}/admin/categories/reorder`, payload);
      // Trigger refresh by calling moveCategoryOrder with null (if available)
      // Otherwise, just update locally
      if (moveCategoryOrder) moveCategoryOrder(null, 'refresh');
    } catch (e) { console.error(e); }
    finally { setReorderSaving(false); }
  }, [sortedCats, moveCategoryOrder]);

  const activeCats = categories.filter(c => c.is_active !== false);
  const inactiveCats = categories.filter(c => c.is_active === false);

  const toggleCategoryActive = (catId, currentActive) => {
    updateCategory(catId, { is_active: !currentActive });
  };

  return (
    <>
      <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3] mb-4 flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-[#2D241D]">
            Categories ({activeCats.length} actives{inactiveCats.length > 0 ? `, ${inactiveCats.length} masquees` : ''})
          </h3>
          <p className="text-xs text-[#6E5E52]">
            Maintenez <GripVertical className="w-3 h-3 inline text-[#D4A853]" /> et glissez pour reordonner
            {reorderSaving && <span className="ml-2 text-[#E16833]">Sauvegarde...</span>}
          </p>
        </div>
        <Dialog open={showNewCat} onOpenChange={setShowNewCat}>
          <DialogTrigger asChild>
            <Button className="bg-[#0A422D] hover:bg-[#083323] text-white rounded-xl" data-testid="add-category-btn">
              <Plus className="w-4 h-4 mr-2" /> Ajouter
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md bg-white rounded-3xl admin-theme">
            <DialogHeader><DialogTitle className="text-[#2D241D]">Nouvelle categorie</DialogTitle></DialogHeader>
            <div className="space-y-4 p-4">
              <div>
                <label className="text-sm font-medium text-[#2D241D] mb-1 block">Nom (anglais)</label>
                <Input value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })}
                  placeholder="ex: Cocktails" className="rounded-xl border-[#E5DCD3]" data-testid="new-cat-name" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2D241D] mb-1 block">Nom (francais)</label>
                <Input value={newCat.name_fr} onChange={(e) => setNewCat({ ...newCat, name_fr: e.target.value })}
                  placeholder="ex: Cocktails" className="rounded-xl border-[#E5DCD3]" data-testid="new-cat-name-fr" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2D241D] mb-1.5 block">Affectation</label>
                <SectionToggle value={newCat.section || 'cuisine'}
                  onChange={(s) => setNewCat({ ...newCat, section: s })}
                  testIdPrefix="new-cat" />
              </div>
              <Button onClick={createCategory} className="w-full bg-[#0A422D] hover:bg-[#083323] text-white rounded-xl" data-testid="save-new-cat-btn">
                <Save className="w-4 h-4 mr-2" /> Creer la categorie
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={catIds} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {sortedCats.map((cat) => (
              <SortableCategoryRow key={cat.id} cat={cat}
                editingCat={editingCat} setEditingCat={setEditingCat}
                updateCategory={updateCategory} deleteCategory={deleteCategory}
                toggleCategoryActive={toggleCategoryActive} adminRole={adminRole} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </>
  );
};
